<template>
  <div class="login-page">
    <!-- Brand: the app's sidebar colours and the collection cycle -->
    <aside class="brand-panel">
      <div class="brand">
        <img src="/prexens-mark.svg" alt="" width="44" height="44" />
        <div>
          <strong>Prexens</strong>
          <span>Collection manager</span>
        </div>
      </div>

      <div class="brand-pitch">
        <h1>Toute ta collection, de l’enchère à la revente.</h1>
        <ol class="cycle">
          <li><b>01</b>Repérer les annonces avec la veille</li>
          <li><b>02</b>Suivre les enchères, alerte avant la fin</li>
          <li><b>03</b>Inventorier les lots remportés</li>
          <li><b>04</b>Vendre et mesurer la marge</li>
        </ol>
      </div>

      <p class="brand-foot">Yahoo! Auctions · Mercari · Vinted</p>
    </aside>

    <!-- Form -->
    <main class="form-panel">
      <div class="form-card">
        <header class="form-header">
          <p>Espace personnel</p>
          <h2>Connexion</h2>
          <span>Content de te revoir, connecte-toi pour continuer.</span>
        </header>

        <div v-if="errorMessage" class="alert-error" role="alert">
          <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
          {{ errorMessage }}
        </div>

        <form novalidate @submit.prevent="handleLogin">
          <div class="field" :class="{ 'field--error': errors.username }">
            <label for="username">Identifiant</label>
            <div class="input-wrap">
              <font-awesome-icon class="input-icon" :icon="['fas', 'user']" />
              <input
                id="username"
                v-model.trim="username"
                type="text"
                placeholder="Ton identifiant"
                required
                :disabled="isLoading"
                autocomplete="username"
                @blur="validateUsername"
              />
            </div>
            <span v-if="errors.username" class="field-error">{{
              errors.username
            }}</span>
          </div>

          <div class="field" :class="{ 'field--error': errors.password }">
            <label for="password">Mot de passe</label>
            <div class="input-wrap">
              <font-awesome-icon class="input-icon" :icon="['fas', 'lock']" />
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Ton mot de passe"
                required
                :disabled="isLoading"
                autocomplete="current-password"
                @blur="validatePassword"
              />
              <button
                type="button"
                class="eye-btn"
                :disabled="isLoading"
                :aria-label="
                  showPassword
                    ? 'Masquer le mot de passe'
                    : 'Afficher le mot de passe'
                "
                @click="showPassword = !showPassword"
              >
                <font-awesome-icon
                  :icon="['fas', showPassword ? 'eye-slash' : 'eye']"
                />
              </button>
            </div>
            <span v-if="errors.password" class="field-error">{{
              errors.password
            }}</span>
          </div>

          <label class="remember">
            <input v-model="rememberMe" type="checkbox" :disabled="isLoading" />
            <span>Se souvenir de moi</span>
          </label>

          <button
            type="submit"
            class="submit-btn"
            :disabled="!isFormValid || isLoading"
          >
            <span v-if="isLoading" class="spinner"></span>
            {{ isLoading ? "Connexion en cours…" : "Se connecter" }}
            <span v-if="!isLoading" aria-hidden="true">→</span>
          </button>
        </form>

        <p class="form-footer">
          Besoin d’aide ?
          <a href="mailto:support@prexens.com">Contacter le support</a>
        </p>
      </div>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import { isTokenExpired } from "../auth";
import config from "@/config/config";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faUser,
  faLock,
  faEye,
  faEyeSlash,
  faRightToBracket,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

library.add(
  faUser,
  faLock,
  faEye,
  faEyeSlash,
  faRightToBracket,
  faTriangleExclamation,
);

export default {
  name: "LoginForm",
  components: { FontAwesomeIcon },
  data() {
    return {
      username: "",
      password: "",
      showPassword: false,
      rememberMe: false,
      isLoading: false,
      errorMessage: "",
      errors: {},
    };
  },
  computed: {
    isFormValid() {
      return (
        this.username.trim() &&
        this.password &&
        Object.keys(this.errors).length === 0
      );
    },
  },
  mounted() {
    const token = localStorage.getItem("access_token");
    if (token && !isTokenExpired(token)) {
      this.$router.push(this.redirectAfterLogin());
      return;
    }
    this.loadRememberedCredentials();
    this.$nextTick(() => {
      document.getElementById("username")?.focus();
    });
  },
  methods: {
    redirectAfterLogin() {
      const redirect = String(this.$route.query.redirect || "");
      return redirect.startsWith("/") && !redirect.startsWith("//")
        ? redirect
        : "/dashboard";
    },
    loadRememberedCredentials() {
      const rememberedUsername = localStorage.getItem("remembered_username");
      if (rememberedUsername) {
        this.username = rememberedUsername;
        this.rememberMe = true;
      }
    },

    validateUsername() {
      this.errors = { ...this.errors };
      delete this.errors.username;
      if (!this.username.trim()) {
        this.errors.username = "L'identifiant est requis";
      } else if (this.username.trim().length < 3) {
        this.errors.username =
          "L'identifiant doit contenir au moins 3 caractères";
      }
    },

    validatePassword() {
      this.errors = { ...this.errors };
      delete this.errors.password;
      if (!this.password) {
        this.errors.password = "Le mot de passe est requis";
      }
    },

    validateForm() {
      this.errors = {};
      this.validateUsername();
      this.validatePassword();
      return Object.keys(this.errors).length === 0;
    },

    async handleLogin() {
      this.errorMessage = "";
      if (!this.validateForm()) {
        this.errorMessage = "Veuillez corriger les erreurs dans le formulaire";
        return;
      }
      try {
        this.isLoading = true;
        const response = await axios.post(`${config.apiUrl}/login`, {
          username: this.username.trim(),
          password: this.password,
        });
        const { access_token, refresh_token } = response.data;
        localStorage.setItem("access_token", access_token);
        localStorage.setItem("refresh_token", refresh_token);
        if (this.rememberMe) {
          localStorage.setItem("remembered_username", this.username.trim());
        } else {
          localStorage.removeItem("remembered_username");
        }
        this.$router.push(this.redirectAfterLogin());
      } catch (error) {
        console.error("Erreur de connexion:", error);
        if (error.response) {
          switch (error.response.status) {
            case 401:
              this.errorMessage = "Identifiant ou mot de passe incorrect";
              break;
            case 429:
              this.errorMessage =
                "Trop de tentatives. Veuillez réessayer plus tard";
              break;
            case 500:
              this.errorMessage =
                "Erreur serveur. Veuillez réessayer plus tard";
              break;
            default:
              this.errorMessage =
                "Une erreur est survenue lors de la connexion";
          }
        } else if (error.request) {
          this.errorMessage =
            "Impossible de contacter le serveur. Vérifiez votre connexion internet";
        } else {
          this.errorMessage = "Une erreur inattendue s'est produite";
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {
    username() {
      if (this.errors.username) this.validateUsername();
    },
    password() {
      if (this.errors.password) this.validatePassword();
    },
  },
};
</script>

<style scoped>
.login-page {
  --navy: #101827;
  --mint: #8df0c8;
  --green: #168a64;
  --text: #202b3c;
  --muted: #6f7b8d;
  --line: #e3e8ef;
  display: flex;
  min-height: 100vh;
  background: #f5f7fa;
  color: var(--text);
  font-family: "DM Sans", sans-serif;
}

/* Brand panel */
.brand-panel {
  position: relative;
  display: flex;
  flex: 0 0 min(44%, 600px);
  flex-direction: column;
  justify-content: space-between;
  gap: 40px;
  padding: 44px 56px;
  overflow: hidden;
  background: var(--navy);
  color: #dbe4f0;
}
/* The "P" of the logo, very large and faint */
.brand-panel::after {
  content: "";
  position: absolute;
  right: -120px;
  bottom: -140px;
  width: 460px;
  height: 460px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(141, 240, 200, 0.16) 0%,
    rgba(141, 240, 200, 0) 68%
  );
  pointer-events: none;
}
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.brand strong {
  display: block;
  color: #fff;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: 19px;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.brand span {
  display: block;
  margin-top: 2px;
  color: #77859a;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.brand-pitch {
  position: relative;
  z-index: 1;
  max-width: 440px;
}
.brand-pitch h1 {
  margin: 0 0 32px;
  color: #fff;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1.18;
}
.cycle {
  display: grid;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.cycle li {
  display: flex;
  margin: 0;
  padding: 0;
  align-items: center;
  gap: 14px;
  color: #c9d3df;
  font-size: 14px;
}
.cycle b {
  display: grid;
  width: 34px;
  height: 26px;
  flex: 0 0 34px;
  place-items: center;
  border-radius: 6px;
  background: #253047;
  color: var(--mint);
  font-size: 11px;
}
.brand-foot {
  position: relative;
  z-index: 1;
  margin: 0;
  color: #607087;
  font-size: 12px;
}

/* Form */
.form-panel {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
}
.form-card {
  width: 100%;
  max-width: 420px;
  padding: 36px 36px 28px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 18px 50px rgba(16, 24, 39, 0.07);
}
.form-header {
  margin-bottom: 26px;
}
.form-header p {
  margin: 0 0 8px;
  color: var(--green);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.form-header h2 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.035em;
}
.form-header span {
  display: block;
  margin-top: 6px;
  color: var(--muted);
  font-size: 14px;
}
.alert-error {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  padding: 11px 14px;
  border: 1px solid #f0c9c7;
  border-radius: 8px;
  background: #fdf3f2;
  color: #c84642;
  font-size: 13px;
  font-weight: 600;
}
.field {
  margin-bottom: 16px;
}
.field label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
}
.input-wrap {
  display: flex;
  height: 44px;
  align-items: center;
  border: 1px solid #cfd6df;
  border-radius: 8px;
  background: #fff;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.input-wrap:focus-within {
  border-color: #4aa487;
  box-shadow: 0 0 0 3px rgba(22, 138, 100, 0.12);
}
.input-icon {
  width: 40px;
  flex: 0 0 40px;
  color: #9aa6b6;
  font-size: 13px;
}
.input-wrap input {
  min-width: 0;
  height: 100%;
  flex: 1;
  padding: 0 10px 0 0;
  border: 0;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 14px;
  outline: none;
}
.input-wrap input::placeholder {
  color: #a9b3c1;
}
.input-wrap input:-webkit-autofill,
.input-wrap input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px #fff inset;
  -webkit-text-fill-color: var(--text);
}
.eye-btn {
  width: 40px;
  height: 100%;
  border: 0;
  background: none;
  color: #9aa6b6;
  cursor: pointer;
}
.eye-btn:hover:not(:disabled) {
  color: var(--text);
}
.field--error .input-wrap {
  border-color: #e08b88;
}
.field--error .input-wrap:focus-within {
  box-shadow: 0 0 0 3px rgba(200, 70, 66, 0.1);
}
.field-error {
  display: block;
  margin-top: 5px;
  color: #c84642;
  font-size: 12px;
  font-weight: 600;
}
.remember {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 2px 0 22px;
  color: #4e596a;
  font-size: 13px;
  cursor: pointer;
}
.remember input {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--green);
}
.submit-btn {
  display: flex;
  width: 100%;
  height: 46px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 0;
  border-radius: 8px;
  background: var(--navy);
  color: #fff;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 0.15s,
    box-shadow 0.15s;
}
.submit-btn span[aria-hidden] {
  color: var(--mint);
}
.submit-btn:hover:not(:disabled) {
  background: #1d2a40;
  box-shadow: 0 8px 22px rgba(16, 24, 39, 0.22);
}
.submit-btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}
.submit-btn:focus-visible,
.eye-btn:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 2px;
}
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.form-footer {
  margin: 22px 0 0;
  padding-top: 18px;
  border-top: 1px solid #edf0f4;
  color: var(--muted);
  font-size: 12px;
  text-align: center;
}
.form-footer a {
  margin-left: 4px;
  color: #2e6098;
  font-weight: 700;
  text-decoration: none;
}
.form-footer a:hover {
  text-decoration: underline;
}

@media (max-width: 900px) {
  .login-page {
    flex-direction: column;
  }
  .brand-panel {
    flex: none;
    padding: 22px 20px;
  }
  .brand-pitch,
  .brand-foot,
  .brand-panel::after {
    display: none;
  }
  .form-panel {
    align-items: flex-start;
    padding: 28px 16px 40px;
  }
}
@media (max-width: 480px) {
  .form-card {
    padding: 26px 20px 22px;
  }
  .input-wrap input {
    font-size: 16px; /* no zoom on iOS */
  }
}
@media (prefers-reduced-motion: reduce) {
  .spinner {
    animation: none;
  }
}
</style>
